import React from "react";
import { styled } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  useTheme,
  useMediaQuery,
  Paper,
  Button,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import DashBoardCalendar from "components/Scheduler/dashBoardCalendar";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import nutLogo from "assets/NUT-outlined-icon.png";
import { useGetDashboardQuery } from "state/api";
import StatBox from "components/StatBox";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import Avatar from "@mui/material/Avatar";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import axios from "axios";
import {
  AirportShuttle,
  HolidayVillage,
  School,
  Newspaper,
} from "@mui/icons-material";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

// --- COMPONENTE DE TARJETA ---
const DashboardCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3),
  },
  borderRadius: "1.5rem",
  backgroundColor: theme.palette.background.alt,
  boxShadow: theme.shadows[5],
  height: "100%",
  display: "flex",
  flexDirection: "column",
}));

// --- COMPONENTE PARA TARJETAS MÓVILES ---
const MobileStatCard = ({ icon, value, title }) => (
  <DashboardCard>
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {icon}
      <Box sx={{ ml: 1.5 }}>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="caption">{title}</Typography>
      </Box>
    </Box>
  </DashboardCard>
);

// --- COMPONENTE RESTAURADO PARA LA BARRA DE PROGRESO ---
const MIN = 0;
const MAX = 8;
const normalise = (value) => ((value - MIN) * 100) / (MAX - MIN);

function BorderLinearProgress(props) {
  const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 15,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor:
        theme.palette.grey[theme.palette.mode === "light" ? 300 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: theme.palette.mode === "light" ? "teal" : "#00A19B",
    },
  }));

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%", mr: 1 }}>
        <StyledLinearProgress
          variant="determinate"
          value={normalise(props.value)}
        />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant="body2" color="text.secondary">{`${Math.round(
          props.value
        )}/${MAX}`}</Typography>
      </Box>
    </Box>
  );
}

const Dashboard = () => {
  const theme = useTheme();
  const nav = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [isLoading, setIsLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [data1, setData1] = useState([]);
  const [totalActividades, setTotalActividades] = useState(0);
  const [totalComunidades, setTotalComunidades] = useState([]);
  const [nut, setNut] = useState({});
  const [totalExp, setTotalExp] = React.useState([]);
  const [rankingModalOpen, setRankingModalOpen] = useState(false);
  const [calendarModalOpen, setCalendarModalOpen] = useState(false);

  // --- ESTADO RESTAURADO PARA EL PERÍODO DE PRUEBA ---
  // Cambia a 'trial' para ver la tarjeta de período de prueba.
  const [userStatus, setUserStatus] = useState("regular");
  const [progress, setProgress] = useState(4); // Simulación del progreso

  const handleOpenRankingModal = () => setRankingModalOpen(true);
  const handleCloseRankingModal = () => setRankingModalOpen(false);
  const handleOpenCalendarModal = () => setCalendarModalOpen(true);
  const handleCloseCalendarModal = () => setCalendarModalOpen(false);

  useEffect(() => {
    const fetchedItems = JSON.parse(localStorage.getItem("token"));
    if (fetchedItems) {
      setItems(fetchedItems);
      // En una aplicación real, aquí establecerías el status del usuario:
      // setUserStatus(fetchedItems.status || 'regular');
    }
    const fetchData = async () => {
      try {
        const [convocatoriasRes, nutRes, comunidadesRes, actividadesRes, exp] =
          await Promise.all([
            axios.get("/convocatoria/", { params: { id: 1 } }),
            axios.get("/beneficiariosnut/"),
            axios.get("/gettotalcomunidades/"),
            axios.get("/gettotalactvidades/"),
            axios.get("/experienciaTotal/"),
          ]);
        setData1(convocatoriasRes.data);
        setNut(nutRes.data);
        setTotalComunidades(comunidadesRes.data);
        setTotalActividades(actividadesRes.data[0].total);
        setTotalExp(exp.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const actv = React.useMemo(() => {
    return data1.map((item) => ({
      title: item.nombre,
      startDate: `${item.fecha}T${item.horaEncuentro}`,
      endDate: `${item.fecha}T${item.horaRetorno}`,
      id: item.id,
      location: item.locacion,
    }));
  }, [data1]);

  const getTrophyIcon = (rank) => {
    if (rank === 1)
      return <EmojiEventsIcon sx={{ color: "#FFD700", fontSize: "2rem" }} />;
    if (rank === 2)
      return <EmojiEventsIcon sx={{ color: "#C0C0C0", fontSize: "2rem" }} />;
    if (rank === 3)
      return <EmojiEventsIcon sx={{ color: "#CD7F32", fontSize: "2rem" }} />;
    return null;
  };

  //Routes
  const handleLinkCert = (event, message) => {
    if (message === "cert") nav("/testUserDashboard");
  };

  if (isLoading)
    return (
      <Backdrop
        open={true}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );

  return (
    <>
      <Container maxWidth="lg" sx={{ my: 3 }}>
        <Grid container mt={3} spacing={isSmallScreen ? 2 : 3}>
          <Grid item xs={12}>
            <Typography
              variant={isSmallScreen ? "h4" : "h2"}
              sx={{ fontWeight: "bold" }}
            >
              Bienvenido(a), {items.nombre}
            </Typography>
          </Grid>

          {/* --- TARJETA DE PERÍODO DE PRUEBA (RENDERIZADO CONDICIONAL) --- */}
          {userStatus === "trial" && (
            <Grid item xs={12}>
              <DashboardCard>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  <PolicyOutlinedIcon sx={{ verticalAlign: "middle", mr: 1 }} />
                  Estatus del Período de Prueba
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Actualmente te encuentras en <b>Período de Prueba</b>.
                  Visualiza tu progreso para convertirte en voluntario regular.
                </Typography>
                <BorderLinearProgress value={progress} />
                <Typography mt={2} variant="caption" color="text.secondary">
                  * Debes asistir a un total de <b>8 jornadas</b> en{" "}
                  <b>3 meses</b> para completar el período de prueba.
                </Typography>
              </DashboardCard>
            </Grid>
          )}

          <Grid item md={4} sx={{ display: { xs: "none", md: "block" } }}>
            <DashboardCard>
              <StatBox
                title="Beneficiados por Nutrición"
                value={nut.total || 0}
                icon={
                  <Avatar sx={{ width: 75, height: 75, bgcolor: "teal" }}>
                    {" "}
                    <Box
                      component="img"
                      src={nutLogo}
                      height="45px"
                      width="45px"
                    />{" "}
                  </Avatar>
                }
              />
              <Divider sx={{ my: 2 }} />
              <StatBox
                title="Beneficiados por Salud"
                value={0}
                icon={
                  <Avatar sx={{ width: 75, height: 75, bgcolor: "teal" }}>
                    {" "}
                    <LocalHospitalOutlinedIcon
                      sx={{ color: "white", fontSize: "45px" }}
                    />{" "}
                  </Avatar>
                }
              />
            </DashboardCard>
          </Grid>
          <Grid item md={4} sx={{ display: { xs: "none", md: "block" } }}>
            <DashboardCard>
              <StatBox
                title="Comunidades Alcanzadas"
                value={totalComunidades.length}
                icon={
                  <Avatar sx={{ width: 75, height: 75, bgcolor: "teal" }}>
                    {" "}
                    <HolidayVillage
                      sx={{ color: "white", fontSize: "45px" }}
                    />{" "}
                  </Avatar>
                }
              />
              <Divider sx={{ my: 2 }} />
              <StatBox
                title="Actividades Realizadas"
                value={totalActividades}
                icon={
                  <Avatar sx={{ width: 75, height: 75, bgcolor: "teal" }}>
                    {" "}
                    <AirportShuttle
                      sx={{ color: "white", fontSize: "45px" }}
                    />{" "}
                  </Avatar>
                }
              />
            </DashboardCard>
          </Grid>
          <Grid
            container
            item
            xs={12}
            spacing={2}
            sx={{ display: { xs: "flex", md: "none" } }}
          >
            <Grid item xs={6}>
              <MobileStatCard
                title="Benef. (Nutrición)"
                value={nut.total || 0}
                icon={
                  <Avatar sx={{ bgcolor: "teal" }}>
                    <Box
                      component="img"
                      src={nutLogo}
                      height="24px"
                      width="24px"
                    />
                  </Avatar>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <MobileStatCard
                title="Benef. (Salud)"
                value={0}
                icon={
                  <Avatar sx={{ bgcolor: "teal" }}>
                    <LocalHospitalOutlinedIcon sx={{ fontSize: "24px" }} />
                  </Avatar>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <MobileStatCard
                title="Comunidades"
                value={totalComunidades.length}
                icon={
                  <Avatar sx={{ bgcolor: "teal" }}>
                    <HolidayVillage sx={{ fontSize: "24px" }} />
                  </Avatar>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <MobileStatCard
                title="Actividades"
                value={totalActividades}
                icon={
                  <Avatar sx={{ bgcolor: "teal" }}>
                    <AirportShuttle sx={{ fontSize: "24px" }} />
                  </Avatar>
                }
              />
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Grid
              container
              spacing={isSmallScreen ? 2 : 3}
              sx={{ flexGrow: 1 }}
            >
              <Grid item xs={12}>
                <DashboardCard>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    <Newspaper sx={{ verticalAlign: "middle", mr: 1 }} />
                    Boletín Informativo
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexGrow: 1 }}
                  >
                    Mantente al día con las últimas noticias y anuncios.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      bgcolor: "teal",
                      "&:hover": { bgcolor: "#008080" },
                    }}
                    hidden
                  >
                    Leer más
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      bgcolor: "teal",
                      "&:hover": { bgcolor: "#008080" },
                    }}
                    disabled
                  >
                    Proximamente
                  </Button>
                </DashboardCard>
              </Grid>
              <Grid item xs={12}>
                <DashboardCard>
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                    <School sx={{ verticalAlign: "middle", mr: 1 }} />
                    Certificaciones
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexGrow: 1 }}
                  >
                    Accede a los exámenes para validar tus conocimientos.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      bgcolor: "teal",
                      "&:hover": { bgcolor: "#008080" },
                    }}
                    onClick={(event) => handleLinkCert(event, "cert")}
                  >
                    Ver Exámenes
                  </Button>
                </DashboardCard>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={8}>
            <DashboardCard>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  <CalendarMonthOutlinedIcon
                    sx={{ verticalAlign: "middle", mr: 1 }}
                  />
                  Actividades Programadas
                </Typography>
                <IconButton
                  onClick={handleOpenCalendarModal}
                  title="Ver en pantalla completa"
                >
                  <FullscreenIcon />
                </IconButton>
              </Box>
              <Divider sx={{ my: 1 }} />
              <DashBoardCalendar data={actv} />
            </DashboardCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <DashboardCard>
              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                <EmojiEventsOutlinedIcon
                  sx={{ verticalAlign: "middle", mr: 1 }}
                />
                Top 10 de Voluntarios
              </Typography>
              <Divider sx={{ mb: 1 }} />
              <Box sx={{ overflow: "auto", pr: 1, maxHeight: "350px" }}>
                {totalExp.slice(0, 10).map((volunteer, index) => (
                  <Box
                    key={volunteer.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1.5,
                      my: 0.5,
                      borderRadius: "10px",
                      transition: "background-color 0.3s",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        width: "2.5rem",
                        flexShrink: 0,
                        textAlign: "left",
                      }}
                    >
                      {index + 1}
                    </Typography>
                    <Avatar
                      sx={{ ml: 1, mr: 1.5, flexShrink: 0, bgcolor: "teal" }}
                    >
                      {volunteer.nombre_completo.charAt(0)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {volunteer.nombre_completo}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {volunteer.exp} Puntos
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        width: "2.5rem",
                        flexShrink: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {getTrophyIcon(index + 1)}
                    </Box>
                  </Box>
                ))}
              </Box>
              <Box sx={{ mt: "auto", pt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ bgcolor: "teal", "&:hover": { bgcolor: "#008080" } }}
                  onClick={handleOpenRankingModal}
                >
                  Ver más
                </Button>
              </Box>
            </DashboardCard>
          </Grid>
        </Grid>
      </Container>

      <Dialog
        open={rankingModalOpen}
        onClose={handleCloseRankingModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 16,
            backgroundColor: theme.palette.background.alt,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Ranking Completo de Voluntarios
          {/*<IconButton
            onClick={handleCloseRankingModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>*/}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ maxHeight: "60vh" }}>
            {totalExp.map((volunteer, index) => (
              <Box
                key={volunteer.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  my: 0.5,
                  borderRadius: "10px",
                  transition: "background-color 0.3s",
                  "&:hover": { backgroundColor: theme.palette.action.hover },
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    width: "2.5rem",
                    flexShrink: 0,
                    textAlign: "left",
                  }}
                >
                  {index + 1}
                </Typography>
                <Avatar sx={{ ml: 1, mr: 1.5, flexShrink: 0, bgcolor: "teal" }}>
                  {volunteer.nombre_completo.charAt(0)}
                </Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {volunteer.nombre_completo}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {volunteer.exp} Puntos
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "2.5rem",
                    flexShrink: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {getTrophyIcon(index + 1)}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseRankingModal}
            variant="contained"
            color="error"
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={calendarModalOpen}
        onClose={handleCloseCalendarModal}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          style: {
            borderRadius: 16,
            backgroundColor: theme.palette.background.alt,
            height: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Calendario de Actividades
          <IconButton
            onClick={handleCloseCalendarModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "100%", overflow: "hidden" }}>
          <DashBoardCalendar data={actv} height="100%" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Dashboard;
